import React, { ChangeEvent } from "react";

import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import TextField from "@material-ui/core/TextField";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import { DASH, EMPTY_STRING } from "common/constants";
import Loader from "components/Loader";
import NoContent from "components/NoContent";
import {
  getTrainerFieldContentOrNumber,
  getTrainerTypeAndNumber
} from "utils/trainer";

import useStyles from "./styles";

interface IProps {
  trainers: racehorse360.ITrainer[];
  horseTrainerId: string;
  searchQuery: string;
  isDataLoading: boolean;
  showHorsesOnGround: boolean;
  onChangeSearchQuery: (value: string) => void;
  onTrainerSelect: (trainer: racehorse360.ITrainer) => void;
}

const TrainerSearch = (props: IProps) => {
  const {
    trainers,
    horseTrainerId,
    searchQuery,
    isDataLoading,
    showHorsesOnGround,
    onChangeSearchQuery,
    onTrainerSelect
  } = props;

  const classes = useStyles();

  const shouldShowClearButton = Boolean(searchQuery.length);

  const handleSearchQueryChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChangeSearchQuery(event.currentTarget.value || EMPTY_STRING);
  };

  const handleSearchClose = () => {
    onChangeSearchQuery(EMPTY_STRING);
  };

  const handleTrainerSelect = (trainer: racehorse360.ITrainer) => () => {
    onTrainerSelect(trainer);
  };

  const startAdornment = (
    <InputAdornment position="start">
      <SearchIcon className={classes.searchIcon} />
    </InputAdornment>
  );

  const endAdornment = shouldShowClearButton && (
    <IconButton size="small" onClick={handleSearchClose}>
      <CloseIcon className={classes.clearSearchIcon} />
    </IconButton>
  );

  const renderSearchResults = () => {
    if (isDataLoading) return <Loader />;

    if (!trainers.length) {
      return (
        <NoContent className={classes.noContent}>
          {`No Results for "${searchQuery}"`}
        </NoContent>
      );
    }

    return trainers.map(trainer => {
      const trainerTypeAndNumber = getTrainerTypeAndNumber(trainer);
      const listItemData = [
        getTrainerFieldContentOrNumber(trainer.firstName, trainer),
        getTrainerFieldContentOrNumber(trainer.lastName, trainer),
        trainerTypeAndNumber,
        showHorsesOnGround ? `${trainer.horsesOnGround} horses` : undefined
      ].filter(Boolean);

      return (
        <Button
          key={trainer.id}
          className={classes.listItem}
          onClick={handleTrainerSelect(trainer)}
          disabled={trainer.id === horseTrainerId}
        >
          {listItemData.map((item: string | number, index: number) => (
            <span key={index} className={classes.listItemCell}>
              {item || DASH}
            </span>
          ))}
        </Button>
      );
    });
  };

  return (
    <>
      <TextField
        className={classes.searchInputBase}
        variant="outlined"
        placeholder={"Search Trainers"}
        name={"searchTrainers"}
        value={searchQuery}
        onChange={handleSearchQueryChange}
        autoComplete={"off"}
        InputProps={{
          classes: {
            input: classes.searchInput
          },
          startAdornment: startAdornment,
          endAdornment: endAdornment
        }}
      />
      {searchQuery && (
        <div className={classes.searchResults}>{renderSearchResults()}</div>
      )}
    </>
  );
};

export default React.memo(TrainerSearch);
